import type { MouseEvent } from 'react'
import dayjs from 'dayjs'
import { cn } from '../../utils/cn.utils'
import { PriceInput } from '../ui/input/price.component'
import {
  BUNDLE_SALE_MIN_PERIOD_MONTHS,
  BUNDLE_SALE_PRODUCTS,
  BUNDLE_SALE_TELECOMS,
  type BundleSaleItem,
  type BundleSaleTelecom,
} from '../../lib/apis/dtos/bundle-sale.dto'

const TH =
  'bg-[#1D1B20] text-[#ffffff] border border-[#DED8E1] font-[500] text-[11px] font-medium'
const TD = 'border border-[#DED8E1] bg-[#fff] p-[12px] text-[11px] font-medium'
const COLOR_TD = 'border border-[#DED8E1] px-[8px] py-[12px]'

const TELECOM_META: Record<
  BundleSaleTelecom,
  { label: string; th: string; td: string }
> = {
  SK: {
    label: 'SKT',
    th: 'bg-[#FB5353] text-[#ffffff] border border-[#DED8E1]',
    td: 'bg-[#FB5353]/5',
  },
  KT: {
    label: 'KT',
    th: 'bg-[#369EFF] text-[#ffffff] border border-[#DED8E1]',
    td: 'bg-[#369EFF0D]',
  },
  LG: {
    label: 'LG U+',
    th: 'bg-[#8539F7] text-[#ffffff] border border-[#DED8E1]',
    td: 'bg-[#8539F70D]',
  },
}

/** 번이(nc) / 기변(cd) — 컬럼 순서 */
const SALE_TYPES = [
  { key: 'ncDiscount', label: '번이' },
  { key: 'cdDiscount', label: '기변' },
] as const

interface BundleSaleTableProps {
  items: BundleSaleItem[]
  onChange: (items: BundleSaleItem[]) => void
}

const focusInnerInput = (e: MouseEvent<HTMLElement>) => {
  e.currentTarget.querySelector('input')?.focus()
}

export default function BundleSaleTable({
  items,
  onChange,
}: BundleSaleTableProps) {
  // PriceInput 의 M+N 표기와 동일한 기준 (이번달 남은 일수)
  const remainingDays = dayjs().endOf('month').diff(dayjs(), 'day') + 1

  const findItem = (
    telecom: BundleSaleTelecom,
    option: BundleSaleItem['option'],
  ) => items.find((i) => i.telecom === telecom && i.option === option)

  const updateItem = (
    telecom: BundleSaleTelecom,
    option: BundleSaleItem['option'],
    patch: Partial<BundleSaleItem>,
  ) =>
    onChange(
      items.map((item) =>
        item.telecom === telecom && item.option === option
          ? { ...item, ...patch }
          : item,
      ),
    )

  return (
    <table className="w-full table-fixed border-collapse">
      <colgroup>
        <col style={{ width: '160px' }} />
        {BUNDLE_SALE_TELECOMS.map((telecom) =>
          SALE_TYPES.map(({ key }) => <col key={`${telecom}-${key}`} />),
        )}
      </colgroup>
      <thead>
        <tr>
          <th className={cn(TH, 'h-[72px]')} rowSpan={2}>
            동시판매 상품
          </th>
          {BUNDLE_SALE_TELECOMS.map((telecom) => (
            <th
              key={telecom}
              className={cn(TELECOM_META[telecom].th, 'h-[36px]')}
              colSpan={SALE_TYPES.length}
            >
              {TELECOM_META[telecom].label}
            </th>
          ))}
        </tr>
        <tr>
          {BUNDLE_SALE_TELECOMS.map((telecom) =>
            SALE_TYPES.map(({ key, label }) => (
              <th key={`${telecom}-${key}`} className={cn(TH)}>
                {label} 할인금(만원)
              </th>
            )),
          )}
        </tr>
      </thead>
      <tbody>
        {/* 최소 유지기간 — 고정값(M+13개월) 표시 전용 */}
        <tr>
          <td className={cn(TH, 'text-center')}>최소 유지기간</td>
          {BUNDLE_SALE_TELECOMS.map((telecom) => (
            <td
              key={telecom}
              className={cn(COLOR_TD, 'bg-[#F0F3F4] text-[11px] font-medium')}
              colSpan={SALE_TYPES.length}
            >
              <div className="flex items-center justify-between">
                <span className="flex gap-2 text-[10px] text-[#79747E]">
                  <span>이번달 잔여일 {remainingDays}일</span>
                  <span>+</span>
                </span>
                <span>
                  {BUNDLE_SALE_MIN_PERIOD_MONTHS}
                  <span className="pl-[2px] text-[#C9C5C6]">개월</span>
                </span>
              </div>
            </td>
          ))}
        </tr>
        {BUNDLE_SALE_PRODUCTS.map(({ option, label, badge }) => (
          <tr key={option}>
            <td className={cn(TD)}>
              <div className="flex items-center gap-[6px]">
                <span>{label}</span>
                <span className="rounded-[4px] bg-[#F0F3F4] px-[6px] py-[2px] text-[10px] text-[#49454F]">
                  {badge}
                </span>
              </div>
            </td>
            {BUNDLE_SALE_TELECOMS.map((telecom) => {
              const item = findItem(telecom, option)
              return SALE_TYPES.map(({ key }) => (
                <td
                  key={`${telecom}-${key}`}
                  className={cn(
                    COLOR_TD,
                    TELECOM_META[telecom].td,
                    'cursor-text text-right',
                  )}
                  onClick={focusInnerInput}
                >
                  <PriceInput
                    value={item?.[key] ?? null}
                    errorField={`bundleSales.${telecom}.${option}.${key}`}
                    maxLength={5}
                    allowDecimal
                    suffix="만원 추가 할인"
                    placeholder="판매 안함"
                    onChange={(val) =>
                      updateItem(telecom, option, { [key]: val })
                    }
                  />
                </td>
              ))
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
